import React, { useState } from "react";
import { motion } from "framer-motion";
import { Building2 } from "lucide-react";

/**
 * Image lazy-loaded avec blur-up, aspect-ratio préservé et fallback élégant.
 * - Évite le CLS (Cumulative Layout Shift)
 * - Affiche un skeleton pendant le chargement
 * - Fallback : motif subtil + icône TPCO si l'image manque ou échoue
 */
const SmartImageContent = ({
  src,
  alt = "",
  aspectRatio = "aspect-[4/3]",
  className = "",
  fallbackLabel = "TPCO",
  objectPosition = "object-center",
  loading = "lazy",
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div
        className={`relative w-full ${aspectRatio} ${className} bg-[#F1F3FB] overflow-hidden`}
        data-testid="smart-image-fallback"
        role={alt ? "img" : undefined}
        aria-label={alt ? `${alt} — image indisponible` : undefined}
      >
        {/* Motif géométrique discret */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, #E2E5F1 1px, transparent 1px), radial-gradient(circle at 75% 75%, #E2E5F1 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#9CA3B5]">
          <Building2 className="w-12 h-12" strokeWidth={1.2} />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold">{fallbackLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${aspectRatio} ${className} bg-[#F1F3FB]`}>
      {/* Skeleton sous-jacent */}
      <div className={`absolute inset-0 bg-[#F1F3FB] animate-pulse transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100"}`} />
      <motion.img
        src={src}
        alt={alt}
        loading={loading}
        initial={{ opacity: 0, scale: 1.04 }}
        animate={{ opacity: loaded ? 1 : 0, scale: loaded ? 1 : 1.04 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={`absolute inset-0 w-full h-full object-cover ${objectPosition}`}
      />
    </div>
  );
};

// Une nouvelle URL doit repartir d'un état vierge, même si la précédente a échoué.
export const SmartImage = (props) => <SmartImageContent key={props.src || ""} {...props} />;

export default SmartImage;
